import { useEffect, useMemo, useState } from "react";
import { Card, Row, Col, Table, Typography, Button, Input, Tag, message, Switch, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { goBack, search } from "assets/icons";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import ReactApexcharts from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useAppSelector } from "store";
import { getCampaignList, updateCampaign } from "services/campaignApi";
import { CampaignListItem } from "services/interface";
import { EditOutlined } from "@ant-design/icons";

type TableStatus = "Active" | "Disable" | "Inactive";

interface CampaignTableData {
  id: string;
  campaignName: string;
  sales: number;
  startDate: string;
  endDate: string;
  status: TableStatus;
  raw: CampaignListItem;
}

const Campaign = () => {
  const navigate = useNavigate();
  const profile = useAppSelector((state) => state.auth.profile);
  const factoryId = profile?.user.role.ref_id ?? "";

  const [searchText, setSearchText] = useState("");
  const [campaigns, setCampaigns] = useState<CampaignTableData[]>([]);
  const [loading, setLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);

  const fetchCampaigns = async (searchValue: string = "") => {
    if (!factoryId) return;
    setLoading(true);
    try {
      const res = await getCampaignList({
        search: searchValue,
        sort_by: "create_date",
        sort_direction: "desc",
        offset: 0,
        limit: 10,
        factory_id: factoryId,
      });

      if (res.status !== "success") {
        message.error("ไม่สามารถโหลดข้อมูลแคมเปญได้");
        return;
      }

      const items = res.data.data || [];

      const mapped: CampaignTableData[] = items.map((item) => {
        const status: TableStatus =
          item.is_active && item.status === "active"
            ? "Active"
            : !item.is_active && item.status === "inactive"
            ? "Inactive"
            : "Disable";

        return {
          id: item.id,
          campaignName: item.title,
          sales: item.current_usage ?? 0,
          startDate: item.start_date
            ? new Date(item.start_date).toLocaleDateString("th-TH")
            : "-",
          endDate: item.end_date
            ? new Date(item.end_date).toLocaleDateString("th-TH")
            : "-",
          status,
          raw: item,
        };
      });

      setCampaigns(mapped);
    } catch (error) {
      console.error(error);
      message.error("เกิดข้อผิดพลาดในการโหลดข้อมูลแคมเปญ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factoryId]);

  // Top 3 Sales Data for Chart - sorted by sales descending
  const top3Sales = useMemo(
    () =>
      [...campaigns]
        .sort((a, b) => b.sales - a.sales)
        .slice(0, 3),
    [campaigns]
  );

  // Map colors based on campaign order: B (orange), A (blue), C (green)
  const getCampaignColor = (campaignName: string) => {
    if (campaignName === "แคมเปญ B") return "#FF6B35"; // Orange
    if (campaignName === "แคมเปญ A") return "#4A90E2"; // Blue
    if (campaignName === "แคมเปญ C") return "#52C41A"; // Green
    return "#8c8c8c";
  };

  const chartData = {
    series: [
      {
        name: "ยอดขาย",
        data: top3Sales.map((item) => item.sales),
      },
    ],
    categories: top3Sales.map((item) => item.campaignName),
    colors: top3Sales.map((item) => getCampaignColor(item.campaignName)),
  };

  const chartOptions: ApexOptions = {
    chart: {
      parentHeightOffset: 0,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        barHeight: "70%",
        horizontal: true,
        distributed: true,
      },
    },
    dataLabels: {
      offsetY: 8,
      style: {
        fontWeight: 500,
        fontSize: "0.875rem",
      },
      formatter: (val: number) => `${val}`,
    },
    grid: {
      strokeDashArray: 8,
      xaxis: {
        lines: { show: true },
      },
      yaxis: {
        lines: { show: false },
      },
      padding: {
        top: -18,
        left: 21,
        right: 33,
        bottom: 10,
      },
    },
    colors: chartData.colors,
    legend: { show: false },
    states: {
      hover: {
        filter: { type: "none" },
      },
      active: {
        filter: { type: "none" },
      },
    },
    xaxis: {
      axisTicks: { show: false },
      axisBorder: { show: false },
      categories: chartData.categories,
      labels: {
        formatter: (val) => val,
        style: {
          fontSize: "0.875rem",
        },
      },
      min: 0,
      max: 1000,
      tickAmount: 4,
    },
    yaxis: {
      labels: {
        style: {
          fontWeight: 600,
          fontSize: "0.875rem",
        },
      },
    },
  };

  const handleSearch = () => {
    fetchCampaigns(searchText.trim());
  };

  const handleToggleActive = async (record: CampaignTableData, checked: boolean) => {
    setToggleLoading(record.id);
    try {
      await updateCampaign({
        id: record.id,
        title: record.raw.title,
        description: record.raw.description,
        campaign_type: record.raw.campaign_type,
        status: record.raw.status,
        start_date: record.raw.start_date,
        end_date: record.raw.end_date,
        is_active: checked,
        factory_id: record.raw.factory_id,
        max_usage: record.raw.max_usage,
        max_usage_per_user: record.raw.max_usage_per_user,
        remark: record.raw.remark,
        conditions: record.raw.conditions.map((condition: any) => ({
          condition_type: condition.condition_type,
          priority: condition.priority,
          products: condition.products?.map((p: any) => ({
            product_id: p.product_id,
            sku_variant_id: p.sku_variant_id,
            required_quantity: p.required_quantity,
            priority: p.priority,
          })) || [],
        })),
        rewards: record.raw.rewards.map((reward: any) => ({
          reward_type: reward.reward_type,
          quantity: reward.quantity,
          priority: reward.priority,
          discount_percentage: reward.discount_percentage,
          discount_amount: reward.discount_amount,
          products: reward.products?.map((p: any) => ({
            product_id: p.product_id,
            sku_variant_id: p.sku_variant_id,
            quantity: p.quantity,
            priority: p.priority,
          })) || [],
        })),
      });
      message.success(checked ? 'เปิดใช้งานแคมเปญสำเร็จ' : 'ปิดใช้งานแคมเปญสำเร็จ');
      fetchCampaigns(searchText);
    } catch (error: any) {
      message.error(error?.message || 'เกิดข้อผิดพลาดในการอัพเดทสถานะ');
    } finally {
      setToggleLoading(null);
    }
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case "Active":
        return <Tag color="success">Active</Tag>;
      case "Disable":
        return <Tag color="error">Disable</Tag>;
      case "Inactive":
        return <Tag color="warning">Inactive</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const columns: ColumnsType<CampaignTableData> = [
    {
      title: "S.No",
      width: 80,
      sorter: (a, b) => a.campaignName.localeCompare(b.campaignName),
      render: (_text, _record, index) => {
        return <Typography.Paragraph>{index + 1}</Typography.Paragraph>;
      },
    },
    {
      title: "Campaign Name",
      dataIndex: "campaignName",
      sorter: (a, b) => a.campaignName.localeCompare(b.campaignName),
      render: (text) => {
        return <Typography.Paragraph>{text}</Typography.Paragraph>;
      },
    },
    {
      title: "Sales",
      dataIndex: "sales",
      sorter: (a, b) => a.sales - b.sales,
      render: (text) => {
        return <Typography.Paragraph>{text}</Typography.Paragraph>;
      },
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      sorter: (a, b) => a.startDate.localeCompare(b.startDate),
      render: (text) => {
        return <Typography.Paragraph>{text}</Typography.Paragraph>;
      },
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      sorter: (a, b) => a.endDate.localeCompare(b.endDate),
      render: (text) => {
        return <Typography.Paragraph>{text}</Typography.Paragraph>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (status) => getStatusTag(status),
    },
    {
      title: "Active",
      dataIndex: "raw",
      width: 100,
      align: "center",
      render: (_: any, record: CampaignTableData) => (
        <Switch
          checked={record.raw.is_active}
          loading={toggleLoading === record.id}
          onChange={(checked) => handleToggleActive(record, checked)}
          onClick={(_, e) => e.stopPropagation()}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      align: "center",
      render: (_: any, record: CampaignTableData) => (
        <Tooltip title="แก้ไข">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/campaign/edit/${record.id}`);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Row className="rowgap-vbox" gutter={[24, 0]}>
      <Col span={24}>
        <Card bordered={false} className="criclebox h-full tabs-mobile">
          <HeaderTabs>
            <IconWrapper
              onClick={() => {
                navigate("/campaign");
              }}
            >
              <SpanIcon>{goBack}</SpanIcon>
            </IconWrapper>
            <Typography.Title level={5}>แคมเปญ</Typography.Title>
            <RightIconWrapper>
              <IconWrapper>
                <SpanIcon>{search}</SpanIcon>
              </IconWrapper>
            </RightIconWrapper>
          </HeaderTabs>
          {/* chart bar */}
          {/* <div>
            <div style={{ padding: 10, marginLeft: 10 }}>
            <Typography.Title level={5}>ยอดขาย Top 3</Typography.Title>
            </div>
            <ReactApexcharts options={chartOptions} series={chartData.series} type="bar" height={200} />
          </div> */}
          <div>
            {/* search */}
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 10, gap: 10, alignItems: 'center', paddingRight: 10 }}>
              <Input placeholder="Search" value={searchText} onChange={(e) => setSearchText(e.target.value)} size="small" style={{ width: 400 }} />
              <Button type="primary" onClick={handleSearch}>Search</Button>
            </div>
            {/* table */}
            <Table
              columns={columns}
              dataSource={campaigns}
              rowKey="id"
              loading={loading}
              pagination={false}
              onRow={(record) => ({
                onClick: () => navigate(`/campaign/edit/${record.id}`),
                style: { cursor: 'pointer' },
              })}
            />
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default Campaign;

const HeaderTabs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  flex-direction: row;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 30px;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: #efefef;
  }
`;

const SpanIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const RightIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const SearchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  justify-content: flex-start;
  padding-top: 0;
`;

